import { numberPercentage, numberText } from "../helpers/helpers";
import {
  CALC_TYPE,
  COUNT_VARIABLE_PREFIX,
  SOURCE_STATE_DATA_KEY,
} from "../helpers/config";
import LoadingIcon from "../loadingIcon";
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import { NATIONAL_PARAM } from "../dropdowns/urlParams";

export default function DataTable({
  filteredData,
  demo_header,
  years,
  voteCountYears,
  totalTurnout,
  loading,
  csv_title,
}) {
  const [query] = useQueryParams({
    calc_type: withDefault(StringParam, CALC_TYPE.VOTE_SHARE),
    vote_mode: withDefault(BooleanParam, false),
  });
  const { calc_type, vote_mode } = query;

  // Helper function to get the vote count or vote mode total count
  const getVoteCount = (data, yearCountVariable) => {
    const voteModeCount = `${yearCountVariable.split("_").pop()}_total`;
    return vote_mode
      ? data.value[voteModeCount]
      : data.value[yearCountVariable];
  };

  // Helper function to get registered count year
  const getRegisteredCountYear = (yearCountVariable) =>
    `registered_count_${yearCountVariable.split("_").pop()}`;

  // Helper function to calculate and format the turnout percentage
  const calculatePercentage = (vote_count, total_registered, total_turnout) => {
    return calc_type === CALC_TYPE.TURNOUT_PERCENT
      ? numberPercentage(vote_count, total_registered)
      : numberPercentage(vote_count, total_turnout);
  };

  const exportToCSV = () => {
    // Construct headers for raw counts and percentages
    const headers =
      [
        `"${demo_header}"`,
        ...years.flatMap((year) => [`"${year} Count"`, `"${year} Perc"`]),
      ].join(",") + "\n";

    const rows = filteredData.map((data) => {
      const row = [data.key];

      years.forEach((year, i) => {
        const yearCountVariable =
          voteCountYears[`${COUNT_VARIABLE_PREFIX}${i}`];
        const voteCount = getVoteCount(data, yearCountVariable);
        const registeredCountYear = getRegisteredCountYear(yearCountVariable);
        const totalRegistered = data.value[registeredCountYear];
        const voteModeCount = `${yearCountVariable.split("_").pop()}_total`;
        let state_key = data.value[SOURCE_STATE_DATA_KEY] || NATIONAL_PARAM;
        const totalTurnoutCount = vote_mode
          ? totalTurnout[state_key][voteModeCount]
          : totalTurnout[state_key][yearCountVariable];
        const perc = calculatePercentage(
          voteCount,
          totalRegistered,
          totalTurnoutCount
        );

        // Push both count and percentage to the row
        row.push(voteCount);
        row.push(perc !== null && perc !== undefined ? perc.toFixed(1) : ""); // Add percentage
      });

      return row.join(",");
    });

    // Generate CSV content
    const csvContent = headers + rows.join("\n");

    // Trigger file download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${csv_title} Early Vote Report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="position-relative scroll-x w-100" tabIndex="0">
      {loading && (
        <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center">
          <LoadingIcon />
        </div>
      )}
      <table className="table mb-0" style={{ opacity: loading ? ".2" : "1" }}>
        <thead className="bg-dark text-white">
          <tr>
            <td>{demo_header}</td>
            {years.map((heading, i) => (
              <td key={`heading_${i}`}>{heading}</td>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredData.map((data) => (
            <tr key={data.key}>
              <th>{data.key}</th>
              {years.map((year, i) => {
                let state_key =
                  data.value[SOURCE_STATE_DATA_KEY] || NATIONAL_PARAM;
                // Site would crash when changing view_type but worked on first load
                // This is a hacky way to handle this issue
                // Seems like totalTurnout is refreshed after this component rerenders
                if (!totalTurnout[state_key]) return <></>;
                const yearCountVariable =
                  voteCountYears[`${COUNT_VARIABLE_PREFIX}${i}`];
                const voteCount = getVoteCount(data, yearCountVariable);
                const registeredCountYear =
                  getRegisteredCountYear(yearCountVariable);
                const totalRegistered = data.value[registeredCountYear];
                const voteModeCountTotal = `${yearCountVariable.split("_").pop()}_total`;
                const totalTurnoutCount = vote_mode
                  ? totalTurnout[state_key][voteModeCountTotal]
                  : totalTurnout[state_key][yearCountVariable];
                const perc = calculatePercentage(
                  voteCount,
                  totalRegistered,
                  totalTurnoutCount
                );
                const displayPerc =
                  perc !== null && perc !== undefined ? perc.toFixed(1) : "";

                return (
                  <td data-label={year} key={`year_${i}${data.key}`}>
                    {numberText(voteCount)} {`(${displayPerc}%)`}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-gray-300">
          {Object.keys(totalTurnout).map((state_key) => {
            return (
              <tr key={state_key}>
                <th>
                  Total
                  {state_key !== NATIONAL_PARAM &&
                  Object.keys(totalTurnout).length > 1
                    ? " - " + state_key
                    : ""}
                </th>
                {years.map((year, i) => {
                  const yearCountVariable =
                    voteCountYears[`${COUNT_VARIABLE_PREFIX}${i}`];
                  const voteModeTotal = getVoteCount(
                    { value: totalTurnout[state_key] },
                    yearCountVariable
                  );
                  const registeredCountYear =
                    getRegisteredCountYear(yearCountVariable);
                  const totalRegistered =
                    totalTurnout[state_key][registeredCountYear];
                  const totalPerc = numberPercentage(
                    voteModeTotal,
                    totalRegistered
                  );

                  return (
                    <td key={`total_year_${i}_${state_key}`}>
                      {numberText(voteModeTotal)}{" "}
                      {calc_type === CALC_TYPE.TURNOUT_PERCENT &&
                        `(${totalPerc.toFixed(1)}%)`}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tfoot>
      </table>
      <i
        className="fas fa-download position-absolute"
        style={{
          bottom: "10px",
          right: "10px",
          cursor: "pointer",
          fontSize: "20px",
          color: "#3b5a75",
        }}
        onClick={exportToCSV}
        aria-label="Export to CSV"
        title={"Export to CSV"}
      ></i>
    </div>
  );
}
